// Il motore 3D: una sola scena che resta viva sotto tutte le viste del sito.
// - «esplora»: la Terra al centro, si ruota/ingrandisce, si scelgono gli oggetti;
// - «sfondo»: la Terra si sposta di lato e gira piano, sotto le card di vetro delle altre sezioni.
// Disegna la scena in una texture (con mipmap, per la satinatura) e poi la ricompone a schermo
// con il vetro liquido (vedi vetro/vetro.ts).

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { gstime, jday, sunPos } from 'satellite.js';
import { SFOCATURA_FRAMMENTO, SFOCATURA_VERTICE, Vetro } from '../vetro/vetro';
import { Terra } from './terra';
import { Oggetti } from './oggetti';
import type { SistemaSolare } from './sistema';
import { PIANETI, RAGGIO_SOLE } from './pianeti';
import { posizioneLuna } from './lune';
import type { AlWorker, Catalogo, DaWorker } from './messaggi';
import type { FileMissioni, TraiettoriaAsteroide } from '../../../ingest/tipi';
import { posizioneA } from '../core/asteroidi';
import INDICE_PAESI from './paesi-indice.json';
import { ricaricaSeVecchio } from '../core/ricarica';

export type Modalita = 'esplora' | 'sfondo';
export type Panorama = 'terra' | 'sistema';

export interface CorpoProiettato {
  id: string; nome: string; x: number; y: number; visibile: boolean;
  /** Fuori dallo schermo: la posizione è sul bordo e `angolo` (radianti, 0 = destra) indica dove si trova. */
  fuori?: boolean; angolo?: number;
}

const LEGGERA = matchMedia('(pointer: coarse)').matches || (navigator.hardwareConcurrency ?? 8) <= 4;
const facile = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

export class Motore {
  readonly renderer: THREE.WebGLRenderer;
  readonly scena = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(38, 1, 0.01, 3000);
  readonly controlli: OrbitControls;
  readonly terra: Terra;
  readonly oggetti = new Oggetti();
  readonly vetro: Vetro;
  private readonly bersaglio: THREE.WebGLRenderTarget;
  private readonly composizione = new THREE.Scene();
  private readonly cameraPiatta = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private worker!: Worker;
  /** Tentativi di caricare il catalogo e riavvii del worker (per non insistere all'infinito). */
  private tentativiCatalogo = 0;
  private riavviiWorker = 0;
  private calcoloDal = 0;
  // Satinatura: la scena a 1/4 di risoluzione, sfocata con una gaussiana separabile (orizzontale + verticale, ×2).
  private readonly sfocA: THREE.WebGLRenderTarget;
  private readonly sfocB: THREE.WebGLRenderTarget;
  private readonly sfocMateriale: THREE.ShaderMaterial;
  private readonly sfocScena = new THREE.Scene();
  private readonly orologio = new THREE.Clock();
  private readonly sole = new THREE.Vector3();
  private readonly bagliore: THREE.Sprite;
  private traccia: THREE.Line | null = null;
  /** La Luna nella vista della Terra: posizione e dimensione VERE (60 raggi terrestri, si vede allontanandosi). */
  private readonly luna: THREE.Group;
  private marcatore: THREE.Group | null = null;
  private inquadraDopo = -1;

  // Tempo simulato: parte da adesso e scorre a `velocita`× (1 = tempo reale).
  private tempoSim = Date.now();
  private _velocita = 1;
  /** In diretta il tempo della scena è agganciato all'orologio vero (anche dopo che la scheda è stata in secondo piano). */
  private inDiretta = true;
  private ultimoCalcolo = 0;
  private calcoloInCorso = false;

  modalita: Modalita = 'esplora';
  panorama: Panorama = 'terra';
  private sistema: SistemaSolare | null = null;
  private caricatore = new THREE.TextureLoader();
  private dissolvenza = 1;
  private panoramaObiettivo: Panorama = 'terra';
  private cameraTerra = new THREE.Vector3();
  private missioniInAttesa: FileMissioni | null = null;

  // ── Paesi (Potenze spaziali) ──
  private paesiAttivi = false;
  private paesiIntensita = 0;
  private paesiScelto = -1;
  private mappaPaesi: { dati: Uint8ClampedArray; l: number; h: number } | null = null;
  onPaese: ((a3: string | null) => void) | null = null;

  /** Strato dei paesi colorati sulla Terra; con lo strato attivo il clic sceglie un paese invece di un satellite. */
  modoPaesi(attivo: boolean): void {
    this.paesiAttivi = attivo;
    this.aggiornaSpostamento();
    if (attivo) {
      this.terra.caricaPaesi(this.caricatore);
      this.caricaMappaPaesi();
      this.volaVerso(this.camera.position.clone().normalize().multiplyScalar(Math.max(3.1, this.distanzaIdeale() * 0.85)), 1.4);
    } else this.paesiScelto = -1;
  }

  /** Colore (RGBA 0-255) di ogni paese per codice ISO alpha-3; i paesi assenti restano senza colore. */
  coloraPaesi(colori: Record<string, [number, number, number, number]>): void {
    const dati = this.terra.coloriPaesi.image.data as Uint8Array;
    dati.fill(0);
    INDICE_PAESI.forEach((p, k) => { const c = colori[p.a3]; if (c) dati.set(c, (k + 1) * 4); });
    this.terra.coloriPaesi.needsUpdate = true;
  }

  /** Evidenzia un paese e vola sopra di lui (null = nessuno). */
  scegliPaese(a3: string | null): void {
    const k = a3 ? INDICE_PAESI.findIndex((p) => p.a3 === a3) : -1;
    this.paesiScelto = k >= 0 ? k + 1 : -1;
    this.aggiornaSpostamento();
    if (k >= 0) this.mostraLuogo(INDICE_PAESI[k].lat, INDICE_PAESI[k].lon, Math.max(2.7, this.distanzaIdeale() * 0.75));
  }

  private async caricaMappaPaesi(): Promise<void> {
    if (this.mappaPaesi) return;
    const img = new Image();
    img.src = '/texture/paesi-id.png';
    await img.decode();
    const l = 2048, h = 1024;
    const c = document.createElement('canvas');
    c.width = l; c.height = h;
    const g = c.getContext('2d', { willReadFrequently: true })!;
    g.imageSmoothingEnabled = false;
    g.drawImage(img, 0, 0, l, h);
    this.mappaPaesi = { dati: g.getImageData(0, 0, l, h).data, l, h };
  }

  /** Paese sotto il punto dello schermo (px CSS), o null (mare, spazio, mappa non ancora caricata). */
  paeseIn(x: number, y: number): string | null {
    if (!this.mappaPaesi) return null;
    const ndc = new THREE.Vector2((x / this.larghezza) * 2 - 1, -(y / this.altezza) * 2 + 1);
    const raggio = new THREE.Raycaster();
    raggio.setFromCamera(ndc, this.camera);
    const punto = raggio.ray.intersectSphere(new THREE.Sphere(new THREE.Vector3(), 1), new THREE.Vector3());
    if (!punto) return null;
    // Dalle coordinate della scena a quelle terrestri (si toglie la rotazione della Terra).
    punto.applyAxisAngle(new THREE.Vector3(0, 1, 0), -this.terra.gruppo.rotation.y);
    const lat = Math.asin(THREE.MathUtils.clamp(punto.y, -1, 1));
    const lon = Math.atan2(-punto.z, punto.x);
    const m = this.mappaPaesi;
    const px = Math.min(m.l - 1, Math.floor(((lon + Math.PI) / (2 * Math.PI)) * m.l));
    const py = Math.min(m.h - 1, Math.floor(((Math.PI / 2 - lat) / Math.PI) * m.h));
    const id = m.dati[(py * m.l + px) * 4];
    return id ? INDICE_PAESI[id - 1]?.a3 ?? null : null;
  }

  /** Percorso completo della missione scelta nel sistema solare (null = nessuna). */
  evidenziaMissione(id: string | null): void { this.sistema?.evidenzia(id); }

  /** Missioni interplanetarie da mostrare nel sistema solare (si applicano quando la scena esiste). */
  impostaMissioni(file: FileMissioni): void {
    if (this.sistema) this.sistema.impostaMissioni(file);
    else this.missioniInAttesa = file;
  }
  // Spostamento della Terra nello schermo (frazione di larghezza/altezza) nelle viste di contenuto.
  private spostamento = new THREE.Vector2();
  private spostamentoObiettivo = new THREE.Vector2();
  private volo: { da: THREE.Vector3; a: THREE.Vector3; t: number; durata: number } | null = null;
  private opacitaOggetti = 1;
  scelto = -1;
  seguendo = false;
  /** Transizione verso la camera d'inseguimento (alle spalle del satellite, poco sopra). */
  private inseguimento: { t: number; da: THREE.Vector3; daTarget: THREE.Vector3; offset: THREE.Vector3 } | null = null;
  private dpr = 1;
  private larghezza = 1;
  private altezza = 1;
  private pronto = false;

  onCatalogo: ((c: Catalogo) => void) | null = null;
  onScelta: ((i: number) => void) | null = null;
  onSorvolo: ((i: number, x: number, y: number) => void) | null = null;
  onErrore: ((m: string) => void) | null = null;
  onCorpo: ((id: string | null) => void) | null = null;
  onPassaggi: ((r: Extract<DaWorker, { tipo: 'passaggi' }>) => void) | null = null;

  /** Passaggi dell'oggetto i sopra un osservatore (gradi, km) nei prossimi 5 giorni; la risposta arriva a onPassaggi. */
  passaggi(i: number, lat: number, lon: number, alt: number): void {
    this.invia({ tipo: 'passaggi', indice: i, lat, lon, alt, da: Date.now(), giorni: 5 });
  }

  constructor(canvas: HTMLCanvasElement, radiceUi: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.setClearColor(0x000000, 1);
    this.dpr = Math.min(window.devicePixelRatio || 1, LEGGERA ? 1.5 : 2);
    this.renderer.setPixelRatio(this.dpr);

    this.bersaglio = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
      samples: LEGGERA ? 0 : 4,
    });

    const opzioniSfoc = { type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false };
    this.sfocA = new THREE.WebGLRenderTarget(1, 1, opzioniSfoc);
    this.sfocB = new THREE.WebGLRenderTarget(1, 1, opzioniSfoc);
    this.sfocMateriale = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: SFOCATURA_VERTICE,
      fragmentShader: SFOCATURA_FRAMMENTO,
      depthTest: false,
      depthWrite: false,
      uniforms: { uTex: { value: null }, uPasso: { value: new THREE.Vector2() }, uLod: { value: 0 } },
    });
    const quadSfoc = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.sfocMateriale);
    quadSfoc.frustumCulled = false;
    this.sfocScena.add(quadSfoc);

    this.vetro = new Vetro(radiceUi);
    this.vetro.materiale.uniforms.uScena.value = this.bersaglio.texture;
    this.vetro.materiale.uniforms.uSfocata.value = this.sfocB.texture;
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.vetro.materiale);
    quad.frustumCulled = false;
    this.composizione.add(quad);

    this.terra = new Terra(this.caricatore, this.renderer.capabilities.getMaxAnisotropy(), LEGGERA);
    this.scena.add(this.terra.gruppo);
    this.scena.add(this.oggetti.punti);
    this.scena.add(creaStelle(LEGGERA ? 2500 : 5000));
    this.luna = creaLuna(this.caricatore, this.sole);
    this.scena.add(this.luna);
    this.bagliore = creaBaglioreSole();
    this.scena.add(this.bagliore);

    this.camera.position.set(2.2, 1.6, 3.2);
    this.controlli = new OrbitControls(this.camera, canvas);
    this.controlli.enableDamping = true;
    this.controlli.dampingFactor = 0.06;
    this.controlli.minDistance = 1.25;
    this.controlli.maxDistance = 40;
    this.controlli.rotateSpeed = 0.55;
    this.controlli.zoomSpeed = 0.8;
    this.controlli.enablePan = false;

    this.avviaWorker();
    // Rete tornata (telefono che passa da Wi-Fi a dati): se i satelliti mancano si riprova subito.
    window.addEventListener('online', () => { if (!this.oggetti.catalogo) this.caricaCatalogo(); });

    this.ascoltaPuntatore(canvas);
    this.ridimensiona();
    this.camera.position.setLength(this.distanzaIdeale());
    window.addEventListener('resize', () => this.ridimensiona());
    this.renderer.setAnimationLoop(() => this.fotogramma());
  }

  // ── Tempo ───────────────────────────────────────────────────────────────
  get adesso(): number { return this.tempoSim; }
  get velocita(): number { return this._velocita; }
  set velocita(v: number) { this._velocita = v; if (v !== 1) this.inDiretta = false; }
  tornaAdesso(): void { this.tempoSim = Date.now(); this._velocita = 1; this.inDiretta = true; this.ultimoCalcolo = 0; }

  // ── Modalità e camera ───────────────────────────────────────────────────
  /** Terra (con gli oggetti in orbita) o sistema solare. Il cambio avviene con una dissolvenza al nero. */
  impostaPanorama(p: Panorama): void {
    if (p === this.panoramaObiettivo) return;
    this.panoramaObiettivo = p;
  }

  private applicaPanorama(p: Panorama): void {
    this.panorama = p;
    this.scegli(-1);
    if (p === 'sistema') {
      if (!this.sistema) {
        // Il codice del sistema solare (pianeti, lune, scie) si scarica solo la prima volta che serve: chi guarda
        // solo la Terra non lo carica. Fino all'arrivo la scena è vuota (stelle), poi i pianeti compaiono.
        this.caricaSistema();
      }
      this.cameraTerra.copy(this.camera.position);
      this.controlli.minDistance = RAGGIO_SOLE * 2;
      this.controlli.maxDistance = 320;
      this.controlli.target.set(0, 0, 0);
      const largo = this.larghezza / this.altezza;
      this.camera.position.set(0, 62, 118).multiplyScalar(largo < 1 ? 1.6 : 1);
    } else {
      this.controlli.minDistance = 1.25;
      this.controlli.maxDistance = 40;
      this.controlli.target.set(0, 0, 0);
      this.camera.position.copy(this.cameraTerra.lengthSq() ? this.cameraTerra : new THREE.Vector3(2.2, 1.6, 3.2).setLength(this.distanzaIdeale()));
      // Tornando alla Terra da una data lontana si torna a oggi: SGP4 non è affidabile lontano dall'epoca dei dati.
      if (Math.abs(this.tempoSim - Date.now()) > 7 * 864e5) this.tornaAdesso();
    }
    this.camera.updateProjectionMatrix();
    this.controlli.update();
  }

  private caricamentoSistema: Promise<void> | null = null;
  private caricaSistema(): Promise<void> {
    this.caricamentoSistema ??= import('./sistema').then(({ SistemaSolare }) => {
      this.sistema = new SistemaSolare(this.caricatore, creaStelle(LEGGERA ? 2500 : 5000));
      if (this.missioniInAttesa) { this.sistema.impostaMissioni(this.missioniInAttesa); this.missioniInAttesa = null; }
    });
    return this.caricamentoSistema;
  }

  /** Posizioni a schermo di Sole e pianeti (per le etichette). */
  corpiProiettati(): CorpoProiettato[] {
    const v = new THREE.Vector3();
    const out: CorpoProiettato[] = [];
    const proietta = (pos: THREE.Vector3) => {
      v.copy(pos).project(this.camera);
      return { x: (v.x * 0.5 + 0.5) * this.larghezza, y: (-v.y * 0.5 + 0.5) * this.altezza, visibile: v.z < 1 };
    };
    const aggiungi = (id: string, nome: string, pos: THREE.Vector3) => out.push({ id, nome, ...proietta(pos) });
    if (this.panorama === 'terra') {
      // Asteroide scelto: il segno che si muove (col suo nome) e il punto del passaggio più vicino, se sono in campo.
      if (this.asteroide) {
        const { segno, vicino, dati } = this.asteroide;
        const inCampo = (p: { x: number; y: number; visibile: boolean }) => p.visibile && p.x > 0 && p.x < this.larghezza && p.y > 0 && p.y < this.altezza;
        const ps = proietta(segno.position), pv = proietta(vicino.position);
        out.push({ id: `asteroide:${dati.slug}`, nome: dati.nome, ...ps, visibile: segno.visible && inCampo(ps) });
        out.push({ id: 'asteroide-vicino', nome: dati.nome, ...pv, visibile: inCampo(pv) });
      }
      // Vista della Terra: solo la Luna. In scala vera è a 60 raggi terrestri: quasi sempre fuori campo, quindi
      // l'etichetta resta sul bordo dello schermo e indica la direzione (un clic la mostra).
      const c = this.luna.position.clone().applyMatrix4(this.camera.matrixWorldInverse);
      const p = proietta(this.luna.position);
      // Margini: sotto la testata e sopra comandi e dock, e fuori dai pannelli visibili (ricerca, contatori, schede).
      const m = { s: 24, d: 24, a: 84, b: this.larghezza < 900 ? 250 : 120 };
      for (const el of document.querySelectorAll<HTMLElement>('[data-copre]')) {
        if (!(el.checkVisibility?.() ?? el.offsetParent !== null)) continue;
        const r = el.getBoundingClientRect();
        if (r.width < this.larghezza * 0.6) continue;
        if (r.top + r.height / 2 < this.altezza / 2) m.a = Math.max(m.a, r.bottom + 24);
        else m.b = Math.max(m.b, this.altezza - r.top + 24);
      }
      const dentro = c.z < 0 && p.x > m.s && p.x < this.larghezza - m.d && p.y > m.a && p.y < this.altezza - m.b;
      if (dentro) {
        out.push({ id: 'luna', nome: 'luna', ...p, visibile: !nascostoDallaTerra(this.camera.position, this.luna.position) });
      } else {
        // Direzione sullo schermo (anche se la Luna è alle spalle della camera) e punto sul bordo.
        const dx = c.x, dy = -c.y;
        const cx = this.larghezza / 2, cy = this.altezza / 2;
        const k = Math.min(dx > 0 ? (this.larghezza - m.d - cx) / dx : dx < 0 ? (m.s - cx) / dx : Infinity,
          dy > 0 ? (this.altezza - m.b - cy) / dy : dy < 0 ? (m.a - cy) / dy : Infinity);
        out.push({ id: 'luna', nome: 'luna', x: cx + dx * k, y: cy + dy * k, visibile: true, fuori: true, angolo: Math.atan2(dy, dx) });
      }
      return out;
    }
    if (!this.sistema) return [];
    aggiungi('sole', 'sole', this.sistema.sole.position);
    for (const c of this.sistema.corpi) aggiungi(c.pianeta.id, c.pianeta.nome, c.gruppo.position);
    for (const m of this.sistema.missioni) {
      if (m.segno.visible) aggiungi(`missione:${m.id}`, m.id, m.segno.position);
    }
    // Le lune solo quando, avvicinandosi, si staccano dal loro pianeta sullo schermo.
    const w = new THREE.Vector3();
    for (const l of this.sistema.lune) {
      l.mesh.getWorldPosition(w);
      const p = proietta(w);
      const pianeta = out.find((c) => c.id === l.luna.pianeta);
      const staccata = pianeta ? Math.hypot(p.x - pianeta.x, p.y - pianeta.y) > 34 : false;
      out.push({ id: l.luna.id, nome: l.luna.id, ...p, visibile: p.visibile && staccata });
    }
    return out;
  }

  /** Corpo celeste sotto il punto dello schermo (px CSS), o null. */
  cercaCorpo(x: number, y: number): string | null {
    let migliore: string | null = null;
    let dMin = Infinity;
    for (const c of this.corpiProiettati()) {
      if (!c.visibile) continue;
      const d = Math.hypot(c.x - x, c.y - y);
      if (c.id.startsWith('missione:') || !(c.id === 'sole' || PIANETI.some((p) => p.id === c.id))) {
        // Missioni e lune: punti piccoli, si prendono entro qualche pixel (più largo sul touch).
        if (d < 20 && d < dMin) { dMin = d; migliore = c.id; }
        continue;
      }
      const pianeta = PIANETI.find((p) => p.id === c.id);
      const raggio = c.id === 'sole' ? RAGGIO_SOLE : pianeta?.raggioScena ?? 1;
      const dist = this.camera.position.distanceTo(c.id === 'sole' ? new THREE.Vector3() : this.sistema!.corpi.find((k) => k.pianeta.id === c.id)!.gruppo.position);
      const raggioPx = (raggio / dist) * (this.altezza / (2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2))));
      if (d < Math.max(22, raggioPx * 1.2) && d < dMin) { dMin = d; migliore = c.id; }
    }
    return migliore;
  }

  /** Vista della Terra: la camera si porta dalla parte opposta alla Luna, un po' di lato, così Terra e Luna
   *  (piccola: la scala è vera) stanno nella stessa inquadratura. */
  guardaLuna(): void {
    const m = this.luna.position.clone().normalize();
    const lato = this.camera.position.clone().projectOnPlane(m);
    if (lato.lengthSq() < 1e-6) lato.set(0, 1, 0).projectOnPlane(m);
    lato.normalize();
    // Da qui la Luna è a circa 8° dal centro della Terra (che ne occupa 4°): appena fuori dal bordo, ben visibile.
    this.volaVerso(m.multiplyScalar(-14).add(lato.multiplyScalar(this.larghezza < 900 ? 1.8 : 2.5)), 2.2);
  }

  /** Vola verso un corpo del sistema solare. */
  inquadraCorpo(id: string): void {
    if (!this.sistema) return;
    const c = this.sistema.corpi.find((k) => k.pianeta.id === id);
    const m = id.startsWith('missione:') ? this.sistema.missioni.find((k) => `missione:${k.id}` === id) : undefined;
    const l = this.sistema.lune.find((k) => k.luna.id === id);
    const pos = c ? c.gruppo.position.clone() : m ? m.segno.position.clone() : l ? l.mesh.getWorldPosition(new THREE.Vector3()) : new THREE.Vector3();
    const raggio = c ? c.pianeta.raggioScena : m ? 0.25 : l ? l.raggioScena * 2 : RAGGIO_SOLE;
    const dir = this.camera.position.clone().sub(pos).normalize();
    this.controlli.target.copy(pos);
    this.volaVerso(pos.clone().add(dir.multiplyScalar((raggio * 9 + 4) * this.fattoreSpazio())), 1.4);
  }

  impostaModalita(m: Modalita): void {
    if (m === 'sfondo') this.impostaPanorama('terra');
    if (m === this.modalita) return;
    this.modalita = m;
    this.controlli.enabled = m === 'esplora';
    if (m === 'sfondo') {
      this.scegli(-1);
      this.volaVerso(this.camera.position.clone().normalize().multiplyScalar(this.larghezza > 900 ? 3.4 : 2.6), 1.6);
    } else {
      this.volaVerso(this.camera.position.clone().normalize().multiplyScalar(this.distanzaIdeale()), 1.4);
    }
    this.aggiornaSpostamento();
  }

  /** Distanza della camera perché Terra e orbite basse stiano nello schermo anche in verticale. */
  distanzaIdeale(): number {
    const fovV = THREE.MathUtils.degToRad(this.camera.fov);
    const fovH = 2 * Math.atan(Math.tan(fovV / 2) * (this.larghezza / this.altezza));
    return Math.max(4.4, 1.55 / Math.sin(Math.min(fovV, fovH) / 2));
  }

  /** Segnale luminoso pulsante su un punto della Terra (es. la base di un lancio); null per toglierlo. */
  segnaLuogo(lat: number | null, lon: number | null): void {
    if (this.marcatore) { this.terra.gruppo.remove(this.marcatore); this.marcatore = null; }
    if (lat === null || lon === null) return;
    this.marcatore = creaMarcatore();
    const phi = THREE.MathUtils.degToRad(lat), lam = THREE.MathUtils.degToRad(lon);
    // Coordinate terrestri (il marcatore sta nel gruppo della Terra, quindi ruota con lei).
    const n = new THREE.Vector3(Math.cos(phi) * Math.cos(lam), Math.sin(phi), -Math.cos(phi) * Math.sin(lam));
    this.marcatore.position.copy(n.clone().multiplyScalar(1.002));
    this.marcatore.lookAt(n.clone().multiplyScalar(2));
    this.terra.gruppo.add(this.marcatore);
  }

  /** Porta la camera sopra un punto della Terra (es. la rampa di un lancio). */
  mostraLuogo(lat: number, lon: number, distanza = 3.0): void {
    const phi = THREE.MathUtils.degToRad(lat);
    const lam = THREE.MathUtils.degToRad(lon);
    // ECEF → scena (lon 0 = +X, lon 90°E = -Z), poi rotazione terrestre.
    const v = new THREE.Vector3(Math.cos(phi) * Math.cos(lam), Math.sin(phi), -Math.cos(phi) * Math.sin(lam));
    // Rotazione terrestre calcolata dall'orario, non letta dalla Terra: aprendo la pagina direttamente su un lancio o
    // una base il primo fotogramma non c'è ancora stato e la camera inquadrava il lato sbagliato.
    v.applyAxisAngle(new THREE.Vector3(0, 1, 0), gstime(new Date(this.tempoSim)));
    this.volaVerso(v.multiplyScalar(1 + (distanza - 1) * this.fattoreSpazio()), 1.8);
  }

  /** Vola verso l'oggetto i guardando la sua orbita di tre quarti (dall'alto la si vedrebbe di taglio). */
  inquadra(i: number): void {
    const o = this.oggetti;
    if (!o.pos) { this.inquadraDopo = i; return; } // posizioni non ancora calcolate: appena arrivano
    const p = o.posizioneScena(i, this.tempoSim, new THREE.Vector3());
    if (!p || !o.vel) return;
    const v = new THREE.Vector3(o.vel[i * 3], o.vel[i * 3 + 2], -o.vel[i * 3 + 1]);
    const normale = p.clone().cross(v).normalize();
    const dir = p.clone().normalize().multiplyScalar(0.75).add(normale.multiplyScalar(0.65)).normalize();
    // Abbastanza lontano da vedere l'orbita intera: sul telefono (schermo stretto) serve più distanza.
    this.volaVerso(dir.multiplyScalar(Math.max(p.length() * 2.6, this.distanzaIdeale() * 0.85) * this.fattoreSpazio()), 1.6);
  }

  private volaVerso(a: THREE.Vector3, durata: number): void {
    // Con «riduci movimento» del sistema la camera arriva subito (stesso punto, senza il volo).
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) durata = 0.001;
    this.volo = { da: this.camera.position.clone(), a, t: 0, durata };
  }

  // Asteroide scelto: linea della traiettoria, punto del passaggio più vicino, segno che si muove col tempo e orbita
  // della Luna per confronto (scala vera: è questo che fa capire «più vicino della Luna»).
  private asteroide: { gruppo: THREE.Group; dati: TraiettoriaAsteroide; segno: THREE.Points; vicino: THREE.Points } | null = null;

  /** Porta il tempo della scena a un istante (es. poco prima del passaggio di un asteroide) con la velocità data. */
  impostaTempo(ms: number, velocita = 1): void {
    this.tempoSim = ms;
    this._velocita = velocita;
    this.inDiretta = false;
    this.ultimoCalcolo = 0;
  }

  mostraAsteroide(a: TraiettoriaAsteroide | null): void {
    if (this.asteroide) {
      this.scena.remove(this.asteroide.gruppo);
      this.asteroide.gruppo.traverse((o) => { const m = o as THREE.Mesh; m.geometry?.dispose(); (m.material as THREE.Material | undefined)?.dispose(); });
      this.asteroide = null;
      this.controlli.maxDistance = 40;
    }
    if (!a || !a.punti.length) return;
    const scena = (x: number, y: number, z: number) => new THREE.Vector3(x / 6371, z / 6371, -y / 6371);
    const punti = a.punti.map(([, x, y, z]) => scena(x, y, z));
    let iMin = 0;
    punti.forEach((p, i) => { if (p.lengthSq() < punti[iMin].lengthSq()) iMin = i; });
    const gruppo = new THREE.Group();
    const linea = new THREE.Line(new THREE.BufferGeometry().setFromPoints(punti),
      new THREE.LineBasicMaterial({ color: 0xffb066, transparent: true, opacity: 0.9, depthWrite: false }));
    linea.frustumCulled = false;
    const tondo = (() => {
      const c = document.createElement('canvas'); c.width = c.height = 64;
      const g = c.getContext('2d')!; g.beginPath(); g.arc(32, 32, 28, 0, Math.PI * 2); g.fillStyle = '#fff'; g.fill();
      return new THREE.CanvasTexture(c);
    })();
    const punto = (colore: number, px: number) => new THREE.Points(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3()]),
      new THREE.PointsMaterial({ color: colore, size: px, sizeAttenuation: false, map: tondo, alphaTest: 0.5, depthWrite: false }));
    const vicino = punto(0xffffff, 7);
    vicino.position.copy(punti[iMin]);
    const segno = punto(0xffb066, 12);
    // Orbita della Luna: piano da due posizioni a 6 ore di distanza, raggio della distanza di oggi.
    const jd = jday(new Date(this.tempoSim));
    const [ax, ay, az] = posizioneLuna(jd).equatoriale, [bx, by, bz] = posizioneLuna(jd + 0.25).equatoriale;
    const u = scena(ax, ay, az), w0 = scena(bx, by, bz);
    const normale = u.clone().cross(w0).normalize(), raggio = u.length();
    const e1 = u.clone().normalize(), e2 = normale.clone().cross(e1).normalize();
    const anello = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(Array.from({ length: 180 }, (_, k) => {
      const f = (k / 180) * Math.PI * 2;
      return e1.clone().multiplyScalar(Math.cos(f) * raggio).add(e2.clone().multiplyScalar(Math.sin(f) * raggio));
    })), new THREE.LineBasicMaterial({ color: 0xc9c9c4, transparent: true, opacity: 0.28, depthWrite: false }));
    anello.frustumCulled = false;
    gruppo.add(anello, linea, vicino, segno);
    this.scena.add(gruppo);
    this.asteroide = { gruppo, dati: a, segno, vicino };
    // Camera: di sbieco sul piano della traiettoria, lontana quanto serve a far stare nello schermo il punto più
    // vicino e (se l'asteroide passa entro due distanze lunari) l'orbita della Luna, il paragone che conta.
    const d = punti[iMin].length();
    let n = punti[0].clone().cross(punti[punti.length - 1]);
    if (!Number.isFinite(n.x) || n.lengthSq() < 1e-9) n = new THREE.Vector3(0, 1, 0);
    n.normalize();
    const fovV = THREE.MathUtils.degToRad(this.camera.fov);
    const fovH = 2 * Math.atan(Math.tan(fovV / 2) * (this.larghezza / this.altezza));
    const ampiezza = d < raggio * 2 ? Math.max(d, raggio) * 1.12 : d * 1.25;
    const distanza = Math.max(this.distanzaIdeale() * 1.2, (ampiezza / Math.tan(Math.min(fovV, fovH) / 2)) * this.fattoreSpazio());
    this.controlli.maxDistance = Math.max(40, distanza * 3);
    this.volaVerso(n.multiplyScalar(distanza).add(punti[iMin].clone().normalize().multiplyScalar(-distanza * 0.3)).setLength(distanza), 2);
  }

  private _inDettaglio = false;
  private readonly movimentoRidotto = matchMedia('(prefers-reduced-motion: reduce)').matches;
  /** Con una scheda di dettaglio aperta la Terra si sposta nella parte libera dello schermo. */
  set inDettaglio(v: boolean) { this._inDettaglio = v; this.aggiornaSpostamento(); }

  private aggiornaSpostamento(): void {
    const largo = this.larghezza > 900;
    // Sezioni di contenuto: su schermi larghi Terra a destra (contenuti a sinistra), sul telefono in basso come un
    // orizzonte. Altrove la scena si centra nello spazio lasciato libero dalle schede (vedi misuraCopertura).
    if (this.modalita === 'sfondo' && !this._inDettaglio && !this.paesiAttivi) this.spostamentoObiettivo.set(largo ? 0.24 : 0, largo ? 0 : 0.42);
    else this.spostamentoObiettivo.set(0, 0);
    const c = this.copertura;
    this.spostamentoObiettivo.x += (c.sinistra - c.destra) / 2 / this.larghezza;
    this.spostamentoObiettivo.y += (c.alto - c.basso) / 2 / this.altezza;
  }

  // ── Schede che coprono la scena ─────────────────────────────────────────
  // Gli elementi con `data-copre` (schede di un satellite, di un lancio, di un paese…) coprono parte dello schermo:
  // la scena si centra nello spazio libero e la camera si allontana se lo spazio è poco, così l'oggetto scelto e la
  // sua orbita non finiscono sotto la scheda (sul telefono le schede salgono dal basso fino a metà schermo).
  private copertura = { alto: 0, basso: 0, sinistra: 0, destra: 0 };
  private contaFotogrammi = 0;

  private misuraCopertura(): boolean {
    const W = this.larghezza, H = this.altezza;
    const c = { alto: 0, basso: 0, sinistra: 0, destra: 0 };
    // data-copre="con-scheda": conta solo se c'è anche una scheda aperta (es. ricerca e contatori in alto sul telefono:
    // senza scheda la Terra resta al centro dello schermo).
    const visibili = [...document.querySelectorAll<HTMLElement>('[data-copre]')]
      .filter((el) => el.isConnected && (el.checkVisibility?.() ?? el.offsetParent !== null));
    const conScheda = visibili.some((el) => el.dataset.copre !== 'con-scheda');
    for (const el of visibili) {
      if (el.dataset.copre === 'con-scheda' && !conScheda) continue;
      const r = el.getBoundingClientRect();
      if (r.width < 40 || r.height < 40) continue;
      if (r.width > W * 0.6) {
        // Striscia orizzontale: in alto o in basso secondo dove sta il suo centro.
        if (r.top + r.height / 2 > H / 2) c.basso = Math.max(c.basso, H - r.top);
        else c.alto = Math.max(c.alto, r.bottom);
      } else if (r.left + r.width / 2 > W / 2) c.destra = Math.max(c.destra, W - r.left);
      else c.sinistra = Math.max(c.sinistra, r.right);
    }
    const p = this.copertura;
    const cambiata = Math.abs(c.alto - p.alto) + Math.abs(c.basso - p.basso) + Math.abs(c.sinistra - p.sinistra) + Math.abs(c.destra - p.destra) > 4;
    if (cambiata) { this.copertura = c; this.aggiornaSpostamento(); }
    return cambiata;
  }

  /** Quanto allontanare la camera perché l'inquadratura stia nello spazio libero (1 = schermo intero). */
  private fattoreSpazio(): number {
    this.misuraCopertura();
    const c = this.copertura;
    const libero = Math.min(this.larghezza - c.sinistra - c.destra, this.altezza - c.alto - c.basso);
    return THREE.MathUtils.clamp(Math.min(this.larghezza, this.altezza) / Math.max(120, libero), 1, 1.8);
  }

  // ── Oggetti ─────────────────────────────────────────────────────────────
  scegli(i: number): void {
    this.scelto = i;
    this.oggetti.scelto = i;
    if (this.traccia) { this.scena.remove(this.traccia); this.traccia.geometry.dispose(); this.traccia = null; }
    if (this.seguendo) this.segui(false);
    if (i >= 0) this.invia({ tipo: 'orbita', indice: i, t: this.tempoSim, punti: 240 });
    this.onScelta?.(i);
  }

  /** Camera d'inseguimento: si porta alle spalle dell'oggetto scelto, con la Terra che scorre sotto. */
  segui(attivo: boolean): void {
    const o = this.oggetti;
    if (attivo && this.scelto >= 0 && o.vel) {
      const p = o.posizioneScena(this.scelto, this.tempoSim, new THREE.Vector3());
      if (!p) return;
      const i = this.scelto;
      const v = new THREE.Vector3(o.vel[i * 3], o.vel[i * 3 + 2], -o.vel[i * 3 + 1]).normalize();
      const radiale = p.clone().normalize();
      // Distanza proporzionata alla quota: vicina per l'orbita bassa, più larga per le orbite alte.
      const k = THREE.MathUtils.clamp((p.length() - 1) * 1.4, 0.13, 2.5);
      const offset = radiale.multiplyScalar(k * 0.55).add(v.multiplyScalar(-k));
      this.seguendo = true;
      this.volo = null;
      this.inseguimento = { t: 0, da: this.camera.position.clone(), daTarget: this.controlli.target.clone(), offset };
      this.controlli.minDistance = 0.02;
    } else {
      this.seguendo = false;
      this.inseguimento = null;
      if (this.panorama === 'terra') {
        this.controlli.minDistance = 1.25;
        this.volaVerso(this.camera.position.clone().normalize().multiplyScalar(this.distanzaIdeale()), 1.4);
      }
    }
  }

  private invia(m: AlWorker): void { this.worker.postMessage(m); }

  /** Calcolo delle orbite fuori dal thread principale. Se il worker non parte (file sparito dopo una pubblicazione) la
   *  pagina si ricarica; se si blocca o il sistema lo chiude (telefono senza memoria) se ne avvia uno nuovo. */
  private avviaWorker(): void {
    this.worker?.terminate();
    this.worker = new Worker(new URL('./propagatore.worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent<DaWorker>) => this.messaggio(e.data);
    this.worker.onerror = (e) => {
      e.preventDefault();
      if (!this.oggetti.catalogo && ricaricaSeVecchio()) return;
      this.riavviaWorker();
    };
    this.calcoloInCorso = false;
    this.caricaCatalogo();
  }

  private riavviaWorker(): void {
    if (++this.riavviiWorker > 5) return; // qualcosa di più grave: niente cicli
    this.avviaWorker();
  }

  private caricaCatalogo(): void {
    this.tentativiCatalogo++;
    this.invia({ tipo: 'carica', url: '/api/v1/orbite.json' });
  }

  private messaggio(m: DaWorker): void {
    if (m.tipo === 'catalogo') {
      this.oggetti.impostaCatalogo(m);
      this.onCatalogo?.(m);
      this.ultimoCalcolo = 0;
    } else if (m.tipo === 'posizioni') {
      this.calcoloInCorso = false;
      this.oggetti.impostaPosizioni(m.t, m.pos, m.vel);
      if (this.inquadraDopo >= 0) { const i = this.inquadraDopo; this.inquadraDopo = -1; this.inquadra(i); }
    } else if (m.tipo === 'passaggi') {
      this.onPassaggi?.(m);
    } else if (m.tipo === 'orbita' && m.indice === this.scelto) {
      this.traccia = creaTraccia(m.traccia);
      this.scena.add(this.traccia);
    } else if (m.tipo === 'errore') {
      this.calcoloInCorso = false;
      // Catalogo non scaricato (rete del telefono, intoppo del server): si riprova da soli, sempre più distanziati.
      if (!this.oggetti.catalogo) {
        const attesa = [2_000, 5_000, 15_000, 30_000][Math.min(this.tentativiCatalogo - 1, 3)];
        setTimeout(() => { if (!this.oggetti.catalogo) this.caricaCatalogo(); }, attesa);
      }
      this.onErrore?.(m.messaggio);
    }
  }

  /** Oggetto più vicino al punto dello schermo (px CSS), entro `soglia` px e non nascosto dalla Terra. */
  cerca(x: number, y: number, soglia = 14): number {
    const c = this.oggetti.catalogo;
    if (!c || !this.oggetti.pos) return -1;
    const v = new THREE.Vector3();
    const camPos = this.camera.position;
    let migliore = -1;
    let dMin = soglia * soglia;
    const n = c.tipi.length;
    for (let i = 0; i < n; i++) {
      if (!this.oggetti.tipoVisibile(c.tipi[i])) continue;
      if (!this.oggetti.posizioneScena(i, this.tempoSim, v)) continue;
      const p = v.clone().project(this.camera);
      if (p.z > 1) continue;
      const sx = (p.x * 0.5 + 0.5) * this.larghezza;
      const sy = (-p.y * 0.5 + 0.5) * this.altezza;
      const d = (sx - x) ** 2 + (sy - y) ** 2;
      if (d >= dMin) continue;
      if (nascostoDallaTerra(camPos, v)) continue;
      dMin = d;
      migliore = i;
    }
    return migliore;
  }

  private ascoltaPuntatore(canvas: HTMLCanvasElement): void {
    let giu: { x: number; y: number; t: number } | null = null;
    let ultimoSorvolo = 0;
    canvas.addEventListener('pointerdown', (e) => { giu = { x: e.clientX, y: e.clientY, t: performance.now() }; });
    canvas.addEventListener('pointerup', (e) => {
      if (!giu || this.modalita !== 'esplora') return;
      const mosso = Math.hypot(e.clientX - giu.x, e.clientY - giu.y);
      if (mosso < 6 && performance.now() - giu.t < 500) {
        if (this.paesiAttivi) this.onPaese?.(this.paeseIn(e.clientX, e.clientY));
        else if (this.panorama === 'sistema') this.onCorpo?.(this.cercaCorpo(e.clientX, e.clientY));
        else {
          const corpo = this.cercaCorpo(e.clientX, e.clientY);
          if (corpo) this.onCorpo?.(corpo);
          else this.scegli(this.cerca(e.clientX, e.clientY, e.pointerType === 'touch' ? 26 : 14));
        }
      }
      giu = null;
    });
    canvas.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || this.modalita !== 'esplora' || giu) return;
      if (this.panorama === 'sistema') {
        canvas.style.cursor = this.cercaCorpo(e.clientX, e.clientY) ? 'pointer' : 'grab';
        return;
      }
      if (this.paesiAttivi) {
        canvas.style.cursor = this.paeseIn(e.clientX, e.clientY) ? 'pointer' : 'grab';
        return;
      }
      const ora = performance.now();
      if (ora - ultimoSorvolo < 60) return;
      ultimoSorvolo = ora;
      if (this.cercaCorpo(e.clientX, e.clientY)) { canvas.style.cursor = 'pointer'; this.onSorvolo?.(-1, 0, 0); return; }
      const i = this.cerca(e.clientX, e.clientY);
      this.oggetti.evidenza = i;
      canvas.style.cursor = i >= 0 ? 'pointer' : 'grab';
      this.onSorvolo?.(i, e.clientX, e.clientY);
    });
    canvas.addEventListener('pointerleave', () => { this.oggetti.evidenza = -1; this.onSorvolo?.(-1, 0, 0); });
  }

  // ── Disegno ─────────────────────────────────────────────────────────────
  private sfoca(): void {
    const u = this.sfocMateriale.uniforms;
    const w = this.sfocA.width, hh = this.sfocA.height;
    const passi: [THREE.Texture, THREE.WebGLRenderTarget, number, number, number][] = [
      [this.bersaglio.texture, this.sfocA, 1.5 / w, 0, 2],
      [this.sfocA.texture, this.sfocB, 0, 1.5 / hh, 0],
      [this.sfocB.texture, this.sfocA, 1.5 / w, 0, 0],
      [this.sfocA.texture, this.sfocB, 0, 1.5 / hh, 0],
    ];
    for (const [tex, rt, px, py, lod] of passi) {
      u.uTex.value = tex;
      (u.uPasso.value as THREE.Vector2).set(px, py);
      u.uLod.value = lod;
      this.renderer.setRenderTarget(rt);
      this.renderer.render(this.sfocScena, this.cameraPiatta);
    }
  }

  private ridimensiona(): void {
    // Su iPhone «resize» arriva spesso senza cambi di misura: ridimensionare il canvas lo svuoterebbe.
    if (window.innerWidth === this.larghezza && window.innerHeight === this.altezza) return;
    this.larghezza = window.innerWidth;
    this.altezza = window.innerHeight;
    // Misura del canvas scritta anche nello stile (non 100dvh dal CSS): il browser non lo stira mai da solo.
    this.renderer.setSize(this.larghezza, this.altezza, true);
    this.bersaglio.setSize(Math.floor(this.larghezza * this.dpr), Math.floor(this.altezza * this.dpr));
    const q = (x: number) => Math.max(1, Math.floor((x * this.dpr) / 4));
    this.sfocA.setSize(q(this.larghezza), q(this.altezza));
    this.sfocB.setSize(q(this.larghezza), q(this.altezza));
    this.camera.aspect = this.larghezza / this.altezza;
    this.aggiornaSpostamento();
    this.applicaSpostamento();
  }

  private applicaSpostamento(): void {
    if (this.spostamento.lengthSq() < 1e-8) { this.camera.clearViewOffset(); }
    else {
      this.camera.setViewOffset(this.larghezza, this.altezza,
        -this.spostamento.x * this.larghezza, -this.spostamento.y * this.altezza, this.larghezza, this.altezza);
    }
    this.camera.updateProjectionMatrix();
  }

  private fotogramma(): void {
    const dt = Math.min(this.orologio.getDelta(), 0.1);
    // Sul telefono la barra del browser compare e scompare scorrendo e lo schermo utile cambia altezza per qualche
    // decimo di secondo: «resize» arriva solo alla fine. Si controlla a ogni fotogramma, così il vetro disegnato nel
    // canvas resta allineato a icone e testi (prima la barra di vetro del dock si spostava, le icone no).
    if (window.innerWidth !== this.larghezza || window.innerHeight !== this.altezza) this.ridimensiona();
    if (++this.contaFotogrammi % 6 === 0) this.misuraCopertura();
    const tempo = this.orologio.elapsedTime;
    if (this.inDiretta) this.tempoSim = Date.now();
    else this.tempoSim += dt * 1000 * this._velocita;

    // Nuove posizioni dal worker: più spesso quando il tempo scorre veloce (l'estrapolazione è lineare).
    const intervallo = Math.max(120, 1000 / Math.max(1, this.velocita / 20));
    const oraReale = performance.now();
    // Sorveglianza: se il worker non risponde da 10 s (chiuso dal sistema, bloccato) se ne avvia uno nuovo.
    if (this.calcoloInCorso && oraReale - this.calcoloDal > 10_000) this.riavviaWorker();
    if (this.panorama === 'terra' && this.oggetti.catalogo && !this.calcoloInCorso && oraReale - this.ultimoCalcolo > intervallo) {
      this.calcoloInCorso = true;
      this.calcoloDal = oraReale;
      this.ultimoCalcolo = oraReale;
      // Si calcola un attimo «avanti» per compensare il tempo di calcolo.
      this.invia({ tipo: 'calcola', t: this.tempoSim + 60 * this.velocita });
    }

    const data = new Date(this.tempoSim);
    const gmst = gstime(data);
    const s = sunPos(jday(data)).rsun;
    this.sole.set(s.x, s.z, -s.y).normalize();
    this.terra.aggiorna(gmst, this.sole);
    this.bagliore.position.copy(this.sole).multiplyScalar(900);

    // Transizioni
    this.spostamento.lerp(this.spostamentoObiettivo, 1 - Math.exp(-dt * 3.2));
    this.applicaSpostamento();
    const opacita = this.paesiAttivi ? 0.12 : this.modalita === 'esplora' ? 1 : 0.55;
    this.paesiIntensita += ((this.paesiAttivi ? 1 : 0) - this.paesiIntensita) * (1 - Math.exp(-dt * 4));
    this.terra.impostaPaesi(this.paesiIntensita, this.paesiScelto);
    this.opacitaOggetti += (opacita - this.opacitaOggetti) * (1 - Math.exp(-dt * 3));

    if (this.volo) {
      this.volo.t += dt / this.volo.durata;
      const k = facile(Math.min(1, this.volo.t));
      // Interpolazione «sferica»: direzione e distanza separate, così la camera gira intorno alla Terra.
      const dirDa = this.volo.da.clone().normalize();
      const dirA = this.volo.a.clone().normalize();
      const dist = THREE.MathUtils.lerp(this.volo.da.length(), this.volo.a.length(), k);
      const q = new THREE.Quaternion().setFromUnitVectors(dirDa, dirA);
      const qk = new THREE.Quaternion().slerp(q, k);
      this.camera.position.copy(dirDa.applyQuaternion(qk).multiplyScalar(dist));
      if (!this.seguendo && this.panorama === 'terra') this.controlli.target.lerp(new THREE.Vector3(), 1 - Math.exp(-dt * 5));
      if (this.volo.t >= 1) this.volo = null;
    } else if (this.modalita === 'sfondo' && !this._inDettaglio && !this.movimentoRidotto) {
      // Giro lento decorativo dello sfondo. Fermo con una scheda aperta (lancio, razzo, base): il punto segnato
      // deve restare dove la camera l'ha portato, non scivolare via mentre si legge.
      this.camera.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), dt * 0.035);
    }

    if (this.seguendo && this.scelto >= 0) {
      const p = this.oggetti.posizioneScena(this.scelto, this.tempoSim, new THREE.Vector3());
      if (p && this.inseguimento) {
        const s = this.inseguimento;
        s.t += dt / 1.8;
        const k = facile(Math.min(1, s.t));
        // La camera gira intorno alla Terra (non la attraversa) mentre si avvicina al satellite.
        const arrivo = p.clone().add(s.offset);
        const dirDa = s.da.clone().normalize(), dirA = arrivo.clone().normalize();
        const qk = new THREE.Quaternion().slerp(new THREE.Quaternion().setFromUnitVectors(dirDa, dirA), k);
        this.camera.position.copy(dirDa.applyQuaternion(qk).multiplyScalar(THREE.MathUtils.lerp(s.da.length(), arrivo.length(), k)));
        this.controlli.target.lerpVectors(s.daTarget, p, k);
        if (s.t >= 1) this.inseguimento = null;
      } else if (p) {
        const delta = p.clone().sub(this.controlli.target);
        this.controlli.target.add(delta);
        this.camera.position.add(delta);
      }
    } else if (!this.volo && this.panorama === 'terra' && this.controlli.target.lengthSq() > 1e-6) {
      this.controlli.target.multiplyScalar(1 - (1 - Math.exp(-dt * 3)));
    }
    if (!this.volo && !this.inseguimento) this.controlli.update();
    else this.camera.lookAt(this.controlli.target);

    // Dissolvenza tra Terra e sistema solare: si scende al nero, si cambia scena, si risale.
    const cambio = this.panoramaObiettivo !== this.panorama;
    this.dissolvenza = Math.max(0, Math.min(1, this.dissolvenza + (cambio ? -dt * 4 : dt * 2.5)));
    if (cambio && this.dissolvenza === 0) this.applicaPanorama(this.panoramaObiettivo);
    this.vetro.materiale.uniforms.uEsposizione.value = this.dissolvenza;
    // Dietro ai contenuti la scena si attenua: i testi restano leggibili anche sopra la Terra illuminata.
    const fondo = this.vetro.materiale.uniforms.uFondo;
    fondo.value += ((this.modalita === 'sfondo' ? 0.62 : 1) - fondo.value) * (1 - Math.exp(-dt * 4));
    if (this.panorama === 'sistema' && this.sistema) this.sistema.aggiorna(jday(data), dt);

    if (this.marcatore) {
      const k = (tempo * 0.7) % 1;
      const onda = this.marcatore.children[1] as THREE.Mesh;
      onda.scale.setScalar(1 + k * 3.2);
      (onda.material as THREE.MeshBasicMaterial).opacity = 0.75 * (1 - k);
    }
    this.oggetti.aggiorna(this.tempoSim, this.dpr, this.opacitaOggetti);
    if (this.panorama === 'terra') {
      // ECI equatoriale (km) → scena (raggi terrestri): x, z, −y come per i satelliti.
      const [lx, ly, lz] = posizioneLuna(jday(data)).equatoriale;
      this.luna.position.set(lx / 6371, lz / 6371, -ly / 6371);
      this.luna.lookAt(0, 0, 0);
      if (this.asteroide) {
        const p = posizioneA(this.asteroide.dati, this.tempoSim);
        this.asteroide.segno.visible = p !== null;
        if (p) this.asteroide.segno.position.set(p[0] / 6371, p[2] / 6371, -p[1] / 6371);
      }
    }
    this.vetro.aggiorna(dt, tempo, this.larghezza, this.altezza, this.dpr);

    this.renderer.setRenderTarget(this.bersaglio);
    this.renderer.render(this.panorama === 'sistema' && this.sistema ? this.sistema.scena : this.scena, this.camera);
    if (this.vetro.numero > 0) this.sfoca();
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.composizione, this.cameraPiatta);
    // Il vetro CSS di riserva resta finché lo shader non ha disegnato il primo fotogramma (sui telefoni la
    // compilazione richiede anche un secondo): il cambio avviene nello stesso fotogramma, senza lampeggio.
    if (!this.pronto) {
      this.pronto = true;
      document.documentElement.classList.add('vetro-gl');
    }
  }
}

function nascostoDallaTerra(cam: THREE.Vector3, p: THREE.Vector3): boolean {
  // Il segmento camera→oggetto attraversa la sfera di raggio 1?
  const d = p.clone().sub(cam);
  const lung = d.length();
  d.divideScalar(lung);
  const b = cam.dot(d);
  const c = cam.lengthSq() - 1;
  const delta = b * b - c;
  if (delta < 0) return false;
  const t = -b - Math.sqrt(delta);
  return t > 0 && t < lung;
}

function creaStelle(n: number): THREE.Points {
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const dim = new Float32Array(n);
  const c = new THREE.Color();
  for (let i = 0; i < n; i++) {
    const u = Math.random() * 2 - 1;
    const a = Math.random() * Math.PI * 2;
    const r = Math.sqrt(1 - u * u);
    pos.set([r * Math.cos(a) * 1500, u * 1500, r * Math.sin(a) * 1500], i * 3);
    // Temperature di colore plausibili: dalle stelle rosse alle azzurre, quasi tutte bianche.
    const temp = Math.random();
    c.setHSL(temp < 0.15 ? 0.07 : temp > 0.85 ? 0.6 : 0.12, temp < 0.15 || temp > 0.85 ? 0.5 : 0.1, 0.85);
    col.set([c.r, c.g, c.b], i * 3);
    dim[i] = Math.pow(Math.random(), 6) * 3.2 + 0.7;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aDim', new THREE.BufferAttribute(dim, 1));
  const m = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    vertexColors: true,
    uniforms: {},
    vertexShader: /* glsl */ `
      attribute float aDim; varying vec3 vC; varying float vA;
      void main() { vC = color; vA = clamp(aDim / 3.0, 0.25, 1.0);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_PointSize = aDim; }`,
    fragmentShader: /* glsl */ `
      varying vec3 vC; varying float vA;
      void main() { float r = length(gl_PointCoord - 0.5); float a = smoothstep(0.5, 0.0, r) * vA;
        gl_FragColor = vec4(vC * a * 0.9, a); }`,
  });
  const p = new THREE.Points(g, m);
  p.frustumCulled = false;
  return p;
}

function creaBaglioreSole(): THREE.Sprite {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0, 'rgba(255,255,250,1)');
  grad.addColorStop(0.08, 'rgba(255,252,240,0.9)');
  grad.addColorStop(0.18, 'rgba(255,236,200,0.18)');
  grad.addColorStop(0.5, 'rgba(255,220,170,0.04)');
  grad.addColorStop(1, 'rgba(255,210,160,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 256);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }));
  s.scale.setScalar(70);
  return s;
}

function creaMarcatore(): THREE.Group {
  const g = new THREE.Group();
  const punto = new THREE.Mesh(new THREE.CircleGeometry(0.011, 32), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false }));
  const onda = new THREE.Mesh(new THREE.RingGeometry(0.014, 0.018, 48), new THREE.MeshBasicMaterial({
    color: 0xffffff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  }));
  g.add(punto, onda);
  g.renderOrder = 5;
  return g;
}

function creaTraccia(traccia: Float32Array): THREE.Line {
  const n = traccia.length / 3;
  const pos = new Float32Array(n * 3);
  const t = new Float32Array(n);
  for (let k = 0; k < n; k++) {
    const x = traccia[k * 3], y = traccia[k * 3 + 1], z = traccia[k * 3 + 2];
    pos.set([x / 6371, z / 6371, -y / 6371], k * 3);
    t[k] = k / (n - 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aT', new THREE.BufferAttribute(t, 1));
  const m = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */ `attribute float aT; varying float vT;
      void main() { vT = aT; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `varying float vT;
      void main() { gl_FragColor = vec4(vec3(0.85, 0.9, 1.0), 0.75 * (1.0 - vT) + 0.12); }`,
  });
  const l = new THREE.Line(g, m);
  l.frustumCulled = false;
  l.renderOrder = 4;
  return l;
}

/** La Luna per la vista della Terra: raggio vero (1.737 km = 0,2727 raggi terrestri), illuminata dal Sole vero.
 *  La faccia visibile guarda la Terra (rotazione sincrona): il gruppo punta la Terra, la texture ha il centro su +x. */
function creaLuna(caricatore: THREE.TextureLoader, sole: THREE.Vector3): THREE.Group {
  const tex = caricatore.load('/texture/luna-1024.webp');
  tex.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(0.2727, 48, 24), new THREE.ShaderMaterial({
    uniforms: { uTex: { value: tex }, uSole: { value: sole } },
    vertexShader: /* glsl */ `varying vec2 vUv; varying vec3 vN;
      void main() { vUv = uv; vN = normalize(mat3(modelMatrix) * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `uniform sampler2D uTex; uniform vec3 uSole; varying vec2 vUv; varying vec3 vN;
      void main() {
        vec3 c = texture2D(uTex, vUv).rgb;
        float luce = max(dot(normalize(vN), normalize(uSole)), 0.0);
        gl_FragColor = vec4(c * (0.02 + 1.15 * luce), 1.0);
        #include <colorspace_fragment>
      }`,
  }));
  mesh.rotation.y = -Math.PI / 2;
  const g = new THREE.Group();
  g.add(mesh);
  return g;
}
